import React, { useState, useEffect } from 'react';
import {
    Alert,
    Row,
    Col,
    Card,
    CardBody,
    Button,
    Input,
    Spinner,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from 'reactstrap';
import { Link, withRouter } from 'react-router-dom';
import MetaTags from 'react-meta-tags';
import SweetAlert from 'react-bootstrap-sweetalert';
import PropTypes from 'prop-types';

// Import Breadcrumb
import Breadcrumbs from "../../components/Common/Breadcrumb";

// Import Datatable
import "../../assets/scss/datatables.scss";

// Import API helper
import httprequester from '../../lib/httprequester';

// Status badge component
const StatusBadge = ({ status }) => {
    const getStatusColor = (status) => {
        switch (status) {
            case 'UPCOMING': return '#ffc107';
            case 'ONGOING': return '#198754';
            case 'COMPLETED': return '#6c757d';
            default: return '#f8f9fa';
        }
    };

    const getTextColor = (status) => {
        switch (status) {
            case 'UPCOMING': return '#000';
            case 'ONGOING': return '#fff';
            case 'COMPLETED': return '#fff';
            default: return '#000';
        }
    };

    return (
        <div
            style={{
                display: 'inline-block',
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                fontWeight: '700',
                lineHeight: '1',
                color: getTextColor(status),
                backgroundColor: getStatusColor(status),
                borderRadius: '0.375rem',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                verticalAlign: 'baseline'
            }}
        >
            {status}
        </div>
    );
};

StatusBadge.propTypes = {
    status: PropTypes.string.isRequired
};

// Action buttons component
const ActionButtons = ({ webinar, onEdit, onTogglePublish, onView }) => (
    <div className="d-flex gap-1">
        <Button
            color="info"
            size="sm"
            onClick={() => onView(webinar.webinar_id)}
            title="View Details"
        >
            <i className="bx bx-show"></i>
        </Button>
        <Button
            color="primary"
            size="sm"
            onClick={() => onEdit(webinar.webinar_id)}
            title="Edit Webinar"
        >
            <i className="bx bx-edit"></i>
        </Button>
        {(() => {
            const isPublished = webinar?.is_published === 1 || webinar?.is_published === true || webinar?.is_published === '1';
            return (
                <Button
                    color={isPublished ? 'warning' : 'success'}
                    size="sm"
                    onClick={() => onTogglePublish(webinar)}
                    title={isPublished ? 'Unpublish Webinar' : 'Publish Webinar'}
                >
                    <i className={`bx ${isPublished ? 'bx-x-circle' : 'bx-check-circle'}`}></i>
                </Button>
            );
        })()}
    </div>
);

ActionButtons.propTypes = {
    webinar: PropTypes.shape({
        webinar_id: PropTypes.number.isRequired,
        title: PropTypes.string.isRequired,
        is_published: PropTypes.oneOfType([PropTypes.bool, PropTypes.number, PropTypes.string])
    }).isRequired,
    onEdit: PropTypes.func.isRequired,
    onTogglePublish: PropTypes.func.isRequired,
    onView: PropTypes.func.isRequired
};

const ListCME = ({ history }) => {

    // State management
    const [webinarList, setWebinarList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [filters, setFilters] = useState({
        status: '',
        search: '',
        page: 1,
        limit: 10,
        sort_by: 'created_at',
        sort_order: 'desc'
    });
    const [searchInput, setSearchInput] = useState('');
    const [pagination, setPagination] = useState({
        current_page: 1,
        total_pages: 1,
        total_items: 0,
        items_per_page: 10
    });
    const [publishModal, setPublishModal] = useState({
        show: false,
        webinar: null
    });

    // Fetch webinars from API
    const fetchWebinarsFromAPI = async (activeFilters) => {
        const f = activeFilters ?? filters;
        setLoading(true);
        setError('');

        try {
            const queryParams = new URLSearchParams({
                page: f.page,
                limit: f.limit,
                sort_by: f.sort_by,
                sort_order: f.sort_order
            });

            if (f.status) {
                queryParams.append('status', f.status);
            }

            if (f.search) {
                queryParams.append('search', f.search);
            }

            const response = await httprequester.getAsync(`webinar/cms/webinars?${queryParams.toString()}`);
            if (response.data && response.data.data) {
                const data = response.data;
                setWebinarList(data.data || []);
                setPagination({
                    current_page: parseInt(data.page) || 1,
                    total_pages: parseInt(data.lastPage) || 1,
                    total_items: parseInt(data.total) || 0,
                    items_per_page: parseInt(data.perPage) || 10
                });
            } else {
                setError('Failed to fetch webinars');
            }
        } catch (err) {
            setError('Error fetching webinars');
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    // Load webinars on component mount and when non-search filters change
    useEffect(() => {
        fetchWebinarsFromAPI(filters);
    }, [filters.page, filters.limit, filters.sort_by, filters.sort_order, filters.status]);

    // Handle filter changes
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1 // Reset to first page when filters change
        }));
    };

    // Handle search (only fetch when Search button is clicked)
    const handleSearch = (e) => {
        e.preventDefault();
        const nextFilters = { ...filters, search: searchInput, page: 1 };
        setFilters(nextFilters);
        fetchWebinarsFromAPI(nextFilters);
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            status: '',
            search: '',
            page: 1,
            limit: 10,
            sort_by: 'created_at',
            sort_order: 'desc'
        });
        setSearchInput('');
    };

    // Navigation handlers
    const handleView = (webinarId) => {
        history.push(`/cme-online/detail/${webinarId}`);
    };

    const handleEdit = (webinarId) => {
        history.push(`/cme-online/edit/${webinarId}`);
    };

    const handleAdd = () => {
        history.push('/cme-online/tambah');
    };

    // Publish / Unpublish webinar
    const handleTogglePublish = async () => {
        if (!publishModal.webinar) return;

        const isPublished = publishModal.webinar?.is_published === 1 || publishModal.webinar?.is_published === true || publishModal.webinar?.is_published === '1';
        const body = isPublished ? { action: 'unpublish' } : { action: 'publish' };

        try {
            const response = await httprequester.putAsync(`webinar/cms/webinars/${publishModal.webinar.webinar_id}/publish`, body);

            if (response.data) {
                // API shape example: { message: string, webinar: { is_published: 0|1, ... } }
                const msg = response.data.message || (isPublished ? 'Webinar unpublished successfully' : 'Webinar published successfully');
                setSuccess(msg);
                setPublishModal({ show: false, webinar: null });
                fetchWebinarsFromAPI();
            } else {
                setError('Failed to update publish status');
            }
        } catch (err) {
            setError('Error updating publish status');
            console.error('Error:', err);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Table columns configuration
    const columns = [
        {
            key: 'thumbnail',
            label: 'Thumbnail',
            render: (webinar) => (
                <img
                    src={webinar.thumbnail || '/default-thumbnail.png'}
                    alt={webinar.title}
                    className="rounded"
                    style={{ width: '50px', height: '50px', objectFit: 'cover' }}
                />
            )
        },
        {
            key: 'title',
            label: 'Title',
            sortable: true,
            render: (webinar) => (
                <div>
                    <div className="fw-bold">{webinar.title}</div>
                    <small className="text-muted">{webinar.slug}</small>
                </div>
            )
        },
        {
            key: 'date',
            label: 'Date & Time',
            sortable: true,
            render: (webinar) => formatDate(webinar.date)
        },
        {
            key: 'status',
            label: 'Status',
            sortable: true,
            render: (webinar) => <StatusBadge status={webinar.status} />
        },
        {
            key: 'quota',
            label: 'Quota',
            sortable: true,
            render: (webinar) => webinar.quota || '-'
        },
        {
            key: 'skp',
            label: 'SKP',
            sortable: true,
            render: (webinar) => webinar.skp || '-'
        },
        {
            key: 'point',
            label: 'Point',
            sortable: true,
            render: (webinar) => webinar.Point || '-'
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (webinar) => (
                <ActionButtons
                    webinar={webinar}
                    onView={handleView}
                    onEdit={handleEdit}
                    onTogglePublish={(webinar) => setPublishModal({ show: true, webinar })}
                />
            )
        }
    ];

    // Clear alerts
    const clearAlert = () => setError('');
    const clearSuccess = () => setSuccess('');

    // PropTypes
    ListCME.propTypes = {
        history: PropTypes.object.isRequired
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <MetaTags>
                    <title>CME Online Management | Ruang Ilmiah CMS</title>
                </MetaTags>

                <div className="container-fluid">
                    <Breadcrumbs title="CME Online" breadcrumbItem="CME Online" />

                    {/* Filters Section */}
                    <Card className="mb-4">
                        <CardBody>
                            <form onSubmit={handleSearch}>
                                <Row>
                                    <Col md="2">
                                        <label className="form-label">Status</label>
                                        <Input
                                            type="select"
                                            value={filters.status}
                                            onChange={(e) => handleFilterChange('status', e.target.value)}
                                        >
                                            <option value="">All Status</option>
                                            <option value="UPCOMING">Upcoming</option>
                                            <option value="ONGOING">Ongoing</option>
                                            <option value="COMPLETED">Completed</option>
                                        </Input>
                                    </Col>
                                    <Col md="3">
                                        <label className="form-label">Search</label>
                                        <Input
                                            type="text"
                                            placeholder="Search by title or description..."
                                            value={searchInput}
                                            onChange={(e) => setSearchInput(e.target.value)}
                                        />
                                    </Col>
                                    <Col md="2">
                                        <label className="form-label">Sort By</label>
                                        <Input
                                            type="select"
                                            value={filters.sort_by}
                                            onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                                        >
                                            <option value="created_at">Created Date</option>
                                            <option value="title">Title</option>
                                            <option value="date">Event Date</option>
                                            <option value="status">Status</option>
                                        </Input>
                                    </Col>
                                    <Col md="2">
                                        <label className="form-label">Sort Order</label>
                                        <Input
                                            type="select"
                                            value={filters.sort_order}
                                            onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                                        >
                                            <option value="desc">Descending</option>
                                            <option value="asc">Ascending</option>
                                        </Input>
                                    </Col>
                                    <Col md="3" className="d-flex align-items-end">
                                        <div className="d-flex gap-2">
                                            <Button color="primary" type="submit" disabled={loading}>
                                                {loading ? <Spinner size="sm" /> : 'Search'}
                                            </Button>
                                            <Button color="secondary" type="button" onClick={resetFilters}>
                                                Reset
                                            </Button>
                                            <Button color="success" type="button" onClick={handleAdd}>
                                                Add Webinar
                                            </Button>
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>

                    {/* Results Section */}
                    <Card>
                        <CardBody>
                            {loading ? (
                                <div className="text-center py-4">
                                    <Spinner color="primary" />
                                    <p className="mt-2">Loading webinars...</p>
                                </div>
                            ) : error ? (
                                <Alert color="danger" isOpen={!!error} toggle={clearAlert}>
                                    {error}
                                </Alert>
                            ) : webinarList.length === 0 ? (
                                <div className="text-center py-4">
                                    <i className="bx bx-calendar-x bx-lg text-muted"></i>
                                    <p className="mt-2">No webinars found</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead className="table-light">
                                            <tr>
                                                {columns.map((column) => (
                                                    <th key={column.key}>
                                                        {column.label}
                                                    </th>
                                                ))}
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {webinarList.map((webinar) => (
                                                <tr key={webinar.webinar_id}>
                                                    {columns.map((column) => (
                                                        <td key={`${webinar.webinar_id}-${column.key}`}>
                                                            {column.render ? column.render(webinar) : webinar[column.key]}
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </CardBody>
                    </Card>

                    {/* Pagination */}
                    {pagination.total_items > 0 && (
                        <Row className="mt-3">
                            <Col className="d-flex justify-content-between align-items-center">
                                <div>
                                    Showing {((pagination.current_page - 1) * pagination.items_per_page) + 1} to{' '}
                                    {Math.min(pagination.current_page * pagination.items_per_page, pagination.total_items)} of{' '}
                                    {pagination.total_items} results
                                </div>
                                <div className="d-flex gap-2">
                                    <Button
                                        color="outline-primary"
                                        disabled={pagination.current_page === 1}
                                        onClick={() => handleFilterChange('page', pagination.current_page - 1)}
                                    >
                                        Previous
                                    </Button>
                                    <span className="px-3 py-2">
                                        Page {pagination.current_page} of {pagination.total_pages}
                                    </span>
                                    <Button
                                        color="outline-primary"
                                        disabled={pagination.current_page >= pagination.total_pages}
                                        onClick={() => handleFilterChange('page', pagination.current_page + 1)}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    )}
                </div>
            </div>

            {/* Publish/Unpublish Confirmation Modal */}
            <Modal isOpen={publishModal.show} toggle={() => setPublishModal({ show: false, webinar: null })}>
                <ModalHeader toggle={() => setPublishModal({ show: false, webinar: null })}>
                    {publishModal.webinar && (publishModal.webinar.is_published === 1 || publishModal.webinar.is_published === true || publishModal.webinar.is_published === '1') ? 'Confirm Unpublish' : 'Confirm Publish'}
                </ModalHeader>
                <ModalBody>
                    {publishModal.webinar && (publishModal.webinar.is_published === 1 || publishModal.webinar.is_published === true || publishModal.webinar.is_published === '1') ? (
                        <span>Are you sure you want to unpublish the webinar &quot;{publishModal.webinar?.title}&quot;?</span>
                    ) : (
                        <span>Are you sure you want to publish the webinar &quot;{publishModal.webinar?.title}&quot;?</span>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setPublishModal({ show: false, webinar: null })}>
                        Cancel
                    </Button>
                    {(publishModal.webinar && (publishModal.webinar.is_published === 1 || publishModal.webinar.is_published === true || publishModal.webinar.is_published === '1')) ? (
                        <Button color="warning" onClick={handleTogglePublish}>
                            Unpublish
                        </Button>
                    ) : (
                        <Button color="success" onClick={handleTogglePublish}>
                            Publish
                        </Button>
                    )}
                </ModalFooter>
            </Modal>

            {/* Success Alert */}
            {success && (
                <SweetAlert
                    success
                    title="Success!"
                    onConfirm={clearSuccess}
                >
                    {success}
                </SweetAlert>
            )}
        </React.Fragment>
    );
};

export default withRouter(ListCME);